// MEXC API Client - Frontend Service
// This client proxies all API calls through the backend to handle authentication securely
import axios from 'axios';
import type { AxiosInstance } from 'axios';
import type {
  MexcApiResponse,
  Position,
  Order,
  Asset,
  ContractInfo,
  Ticker,
  KlineData,
  CreateOrderRequest
} from '../types/mexc';

// API base URL - uses backend proxy endpoints
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/mexc';

export class MexcApiClient {
  private apiClient: AxiosInstance;

  constructor() {
    // Initialize HTTP client for backend API calls
    const defaultTimeout = parseInt(import.meta.env.VITE_API_TIMEOUT) || 30000;

    this.apiClient = axios.create({
      baseURL: API_BASE_URL,
      timeout: defaultTimeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Response interceptor for error handling with retry logic
    this.apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error.config;

        // Initialize retry count if not present
        if (!config._retryCount) {
          config._retryCount = 0;
        }

        // Retry logic with exponential backoff for network errors
        const isTimeoutError = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
        const isServerError = error.response?.status >= 500;
        const maxRetries = 2;
        const shouldRetry = config._retryCount < maxRetries && (isTimeoutError || isServerError);

        if (shouldRetry) {
          config._retryCount++;
          const delay = 1000 * Math.pow(2, config._retryCount - 1);

          await new Promise(resolve => setTimeout(resolve, delay));
          return this.apiClient.request(config);
        }

        return Promise.reject(error);
      }
    );
  }

  // ==================== Public API Methods ====================

  /**
   * Get server time for clock synchronization
   */
  async getServerTime(): Promise<number> {
    try {
      const response = await this.apiClient.get<MexcApiResponse<number>>('/server-time');
      return response.data.data || Date.now();
    } catch (error) {
      console.error('Failed to get server time:', error);
      return Date.now();
    }
  }

  /**
   * Get contract information
   */
  async getContractInfo(symbol?: string): Promise<ContractInfo | ContractInfo[]> {
    const params = symbol ? { symbol } : {};
    const response = await this.apiClient.get<MexcApiResponse<ContractInfo | ContractInfo[]>>(
      '/contract',
      { params }
    );
    return response.data.data || (symbol ? {} as ContractInfo : []);
  }

  /**
   * Get ticker data
   */
  async getTicker(symbol?: string): Promise<Ticker | Ticker[]> {
    const params = symbol ? { symbol } : {};
    const response = await this.apiClient.get<MexcApiResponse<Ticker | Ticker[]>>(
      '/ticker',
      { params }
    );
    return response.data.data || [];
  }

  /**
   * Get kline/candlestick data
   */
  async getKlineData(
    symbol: string,
    interval: string = 'Min1',
    start?: number,
    end?: number
  ): Promise<KlineData> {
    const params: Record<string, any> = { interval };
    if (start) params.start = start;
    if (end) params.end = end;

    try {
      const response = await this.apiClient.get<MexcApiResponse<KlineData>>(
        `/kline/${symbol}`,
        { params }
      );
      return response.data.data || { time: [], open: [], close: [], high: [], low: [], vol: [], amount: [] };
    } catch (error) {
      console.error('Kline API error:', error);
      throw error;
    }
  }

  // ==================== Private API Methods ====================

  /**
   * Get open positions
   */
  async getPositions(): Promise<Position[]> {
    try {
      const response = await this.apiClient.get<MexcApiResponse<Position[]>>('/positions');
      return response.data.data || [];
    } catch (error: any) {
      console.error('❌ Failed to fetch positions:', error.message);
      throw error;
    }
  }

  /**
   * Get history positions (not yet implemented in backend)
   */
  async getHistoryPositions(
    _symbol?: string,
    _page_num: number = 1,
    _page_size: number = 20
  ): Promise<Position[]> {
    // TODO: Implement history positions endpoint if needed
    console.warn('getHistoryPositions not yet implemented in backend');
    return [];
  }

  /**
   * Get open orders
   */
  async getOpenOrders(symbol?: string): Promise<Order[]> {
    const params = symbol ? { symbol } : {};
    try {
      const response = await this.apiClient.get<MexcApiResponse<Order[]>>('/orders', { params });
      return response.data.data || [];
    } catch (error: any) {
      console.error('getOpenOrders API Error:', error.message);
      throw error;
    }
  }

  /**
   * Get account assets
   */
  async getAssets(): Promise<Asset[]> {
    const response = await this.apiClient.get<MexcApiResponse<Asset[]>>('/assets');
    return response.data.data || [];
  }

  /**
   * Get stop orders
   */
  async getStopOrders(symbol?: string): Promise<Order[]> {
    const params = symbol ? { symbol } : {};
    try {
      const response = await this.apiClient.get<MexcApiResponse<Order[]>>('/stop-orders', { params });
      return response.data.data || [];
    } catch (error: any) {
      console.error('getStopOrders API Error:', error.message);
      return [];
    }
  }

  /**
   * Get all orders (not yet implemented in backend)
   */
  async getAllOrders(_symbol?: string): Promise<Order[]> {
    // TODO: Implement all orders endpoint if needed
    console.warn('getAllOrders not yet implemented in backend');
    return [];
  }

  /**
   * Create a new order
   */
  async createOrder(orderRequest: CreateOrderRequest): Promise<{ orderId: number; externalOid?: string }> {
    const response = await this.apiClient.post<MexcApiResponse<{ orderId: number; externalOid?: string }>>(
      '/orders',
      orderRequest
    );

    if (!response.data.success) {
      let errorMsg = response.data.message || 'Failed to create order';
      if (response.data.code === 1002) {
        errorMsg = 'Current contract does not allow placing orders. Please select a different symbol or check MEXC status.';
      }
      throw new Error(errorMsg);
    }

    return response.data.data!;
  }

  /**
   * Cancel an order
   */
  async cancelOrder(orderId: number): Promise<boolean> {
    const response = await this.apiClient.post<MexcApiResponse<boolean>>('/cancel-order', { orderId });
    return response.data.success;
  }

  /**
   * Update position TP/SL
   */
  async updatePositionTPSL(
    positionId: number,
    stopLossPrice?: number,
    takeProfitPrice?: number
  ): Promise<any> {
    // Get price precision from contract info for formatting
    const stopOrders = await this.getStopOrders();
    const existingStopOrder = stopOrders.find(order =>
      String(order.positionId) === String(positionId) && order.state === 1
    );

    let priceScale = 2;
    if (existingStopOrder) {
      try {
        const contractInfo = await this.getContractInfo(existingStopOrder.symbol);
        if (contractInfo) {
          const contractData = Array.isArray(contractInfo) ? contractInfo[0] : contractInfo;
          if (contractData?.priceScale !== undefined) {
            priceScale = contractData.priceScale;
          }
        }
      } catch (error) {
        // Use default priceScale
      }
    }

    const multiplier = Math.pow(10, priceScale);

    // Format prices with proper precision
    const formattedStopLossPrice = stopLossPrice !== undefined && stopLossPrice !== null && stopLossPrice > 0
      ? Math.round(stopLossPrice * multiplier) / multiplier
      : undefined;

    const formattedTakeProfitPrice = takeProfitPrice !== undefined && takeProfitPrice !== null && takeProfitPrice > 0
      ? Math.round(takeProfitPrice * multiplier) / multiplier
      : undefined;

    const response = await this.apiClient.post<MexcApiResponse<any>>('/tpsl', {
      positionId,
      stopLossPrice: formattedStopLossPrice,
      takeProfitPrice: formattedTakeProfitPrice,
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update TP/SL');
    }

    console.log(`✅ Stop-loss updated for position ${positionId}: SL=${formattedStopLossPrice}, TP=${formattedTakeProfitPrice}`);
    return response.data;
  }

  /**
   * Change leverage
   */
  async changeLeverage(symbol: string, leverage: number, openType: 1 | 2): Promise<boolean> {
    const response = await this.apiClient.post<MexcApiResponse<boolean>>('/leverage', {
      symbol,
      leverage,
      openType,
    });
    return response.data.success;
  }
}

// Singleton instance
let mexcApiInstance: MexcApiClient | null = null;

export function getMexcApiClient(): MexcApiClient {
  if (!mexcApiInstance) {
    mexcApiInstance = new MexcApiClient();
  }
  return mexcApiInstance;
}
